/**
 * Created: 2024-03-27
 * Purpose: Form component for initiating password recovery
 * Handles email submission and reCAPTCHA validation
 * Updated: 2024-05-27 - Fixed API endpoint URL to use NEXT_PUBLIC_ environment variable
 * Updated: 2025-01-17 - Fixed to use useApiUrl context for runtime environment variable access
 * Updated: 2025-01-17 - Refactored to use Formik and FieldInput component for better form handling
 */
'use client'
import React, {useState} from 'react'
import {Box, Button, Heading, Text, useToast, VStack, Divider, Link} from '@chakra-ui/react'
import {useSearchParams} from 'next/navigation'
import {useReCaptcha} from '../../contexts/recaptcha.context'
import {useApiUrl} from '../../contexts/api-url.context'
import {Formik, Form} from 'formik'
import {FieldInput} from '@frnt/base/components'
import * as Yup from 'yup'

export interface PasswordRecoverFormProps {
  redirectUri: string
}

interface PasswordRecoverFormData {
  email: string
}

// Validation schema for password recovery
const passwordRecoverValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required')
})

export const PasswordRecoverForm = ({redirectUri}: PasswordRecoverFormProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const toast = useToast()
  const {executeRecaptcha} = useReCaptcha()
  const {apiUrl} = useApiUrl()
  const searchParams = useSearchParams()

  // Get OAuth parameters to preserve in navigation
  const clientId = searchParams.get('client_id')
  const responseType = searchParams.get('response_type')
  const scope = searchParams.get('scope')

  // Construct the login link with OAuth parameters preserved
  const loginHref = () => {
    const params = new URLSearchParams()
    if (redirectUri) params.set('redirect_uri', redirectUri)
    if (clientId) params.set('client_id', clientId)
    if (responseType) params.set('response_type', responseType)
    if (scope) params.set('scope', scope)
    return `/login${params.toString() ? `?${params.toString()}` : ''}`
  }

  const handleSubmit = async (values: PasswordRecoverFormData, {setSubmitting}: {setSubmitting: (isSubmitting: boolean) => void}) => {
    try {
      const recaptchaToken = (await executeRecaptcha('password_recover')) || ''

      const response = await fetch(`${apiUrl}/oauth/password/recover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: values.email,
          recaptcha_token: recaptchaToken,
          redirect_uri: redirectUri
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to initiate password recovery')
      }

      setSubmittedEmail(values.email)
      setIsSubmitted(true)
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <VStack spacing={4} align="stretch">
        <Heading as="h2" size="md" fontWeight="medium">
          Check your email
        </Heading>
        <Text fontSize="sm" color="gray.600">
          If an account exists for {submittedEmail}, you will receive an email with instructions to reset your password.
        </Text>
      </VStack>
    )
  }

  return (
    <Formik initialValues={{email: ''}} validationSchema={passwordRecoverValidationSchema} onSubmit={handleSubmit}>
      {({isSubmitting}) => (
        <Form>
          <VStack spacing={5} align="stretch">
            <FieldInput name="email" label="Email" componentType="input" type="email" placeholder="Enter your email" size="md" />
            <Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText="Submitting" size="md" width="full" height="42px">
              Send Instructions
            </Button>
            <Box position="relative" py={3}>
              <Divider />
            </Box>
            <Text textAlign="center" fontSize="sm" color="gray.600">
              Back to{' '}
              <Link href={loginHref()} color="primary" fontWeight="bold">
                Login
              </Link>
            </Text>
          </VStack>
        </Form>
      )}
    </Formik>
  )
}
